import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { Stack } from '@atlaskit/primitives/compiled';
import Suspense from '@atlaskit/react-ufo/suspense';
import {
	ActionKeys,
	ActionPredicateKeys,
	CONFLUENCE_PAGE_ADD_LABEL_KEY,
	CONFLUENCE_PAGE_COMMENT_KEY,
	CONFLUENCE_PAGE_CREATE_KEY,
	CONFLUENCE_PAGE_MOVE_KEY,
	CONFLUENCE_PAGE_RENAME_KEY,
	CREATE_REQUEST_KEY,
	JIRA_EPICS_SUGGEST_KEY,
	JIRA_FIND_ISSUES_KEY,
	JIRA_ISSUE_DELETE_KEY,
	JIRA_ISSUE_MOVE_KEY,
	HIRING_MANAGER_TOOL_KEY,
	JSM_CREATE_REQUEST_KEY,
	JSM_CREATE_REQUEST_V2_KEY,
	JSM_CREATE_SURVEY_KEY,
	STORE_RECOMMENDATION_ACTION_KEY,
	FOCUS_CREATE_ASK_KEY,
	JIRA_VIEW_CREATE_AND_CONFIGURE_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import { type MessageActionProps } from '@atlassian/conversation-assistant-message-actions-api/types';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import { DefaultActionRendererSkeleton } from '../../common/ui/skeleton';
import { jiraPlansActionData } from '../../common/utils/plan-action-data';

import { ErrorBoundaryWithAnalytics, SuccessEventComponent } from './error-boundary';
import { useActionRenderer } from './use-action-renderer';

const CONVO_AI_DEDICATED_RENDERER_KEYS: ReadonlySet<string> = new Set([
	ActionKeys.ExecutePython,
	ActionKeys.PlanComplete,
	ActionKeys.AskQuestion,
	ActionKeys.AgenticPlanTool,
	ActionKeys.JiraAdminAgent,
	ActionKeys.MultipleChoiceQuestionsTool,
]);

const ROVO_TOOLS_RESUMPTION_MIGRATED_RENDERER_KEYS: ReadonlySet<string> = new Set([
	ActionKeys.GoogleCalendarEventCreate,
	ActionKeys.GoogleDocCreate,
	ActionKeys.SlackSendChannelMessage,
	ActionKeys.ConfluencePageCreate,
	ActionKeys.ConfluencePageComment,
	ActionKeys.ConfluencePageAddLabel,
	ActionKeys.ConfluencePageRename,
	ActionKeys.ConfluencePageMove,
	ActionKeys.AifcAdfUpdate,
]);

const ROVO_TOOLS_RESUMPTION_CONDITIONED_RENDERER_KEYS: ReadonlySet<string> = new Set([
	ActionKeys.GoogleCalendarEventCreate,
	ActionKeys.GoogleDocCreate,
	ActionKeys.SlackSendChannelMessage,
]);

const hasConvoAiDedicatedRenderer = (
	actionKey: string,
	isRovoToolsResumptionMigrationEnabled: boolean,
): boolean => {
	return (
		CONVO_AI_DEDICATED_RENDERER_KEYS.has(actionKey) ||
		(ROVO_TOOLS_RESUMPTION_MIGRATED_RENDERER_KEYS.has(actionKey) &&
			isRovoToolsResumptionMigrationEnabled) ||
		(actionKey === ActionKeys.ConfluenceFolderCreate &&
			fg('confluence_folder_create_action_custom_ui'))
	);
};

const getActionGroupKey = (
	action: MessageAction,
	isRovoToolsResumptionMigrationEnabled: boolean,
): string => {
	const jiraPlansCustomUiActionKey = jiraPlansActionData.getCustomUiActionKey(action);
	const shouldRouteToJiraPlansCustomUi =
		jiraPlansCustomUiActionKey !== undefined &&
		(jiraPlansActionData.isAnthropicActionKey(jiraPlansCustomUiActionKey) ||
			jiraPlansActionData.isLegacyActionKey(jiraPlansCustomUiActionKey));

	if (
		Boolean(action.toolSource) &&
		!hasConvoAiDedicatedRenderer(action.key, isRovoToolsResumptionMigrationEnabled) &&
		action.key !== JSM_CREATE_REQUEST_KEY &&
		action.key !== JSM_CREATE_REQUEST_V2_KEY &&
		action.key !== HIRING_MANAGER_TOOL_KEY &&
		action.key !== CREATE_REQUEST_KEY &&
		!(action.key === FOCUS_CREATE_ASK_KEY && fg('display_components_for_create-focus-ask_skill')) &&
		!(
			action.key === ActionKeys.JiraCreateSprint ||
			action.key === ActionKeys.JiraStartSprint ||
			action.key === ActionKeys.JiraMoveToSprint
		) &&
		!shouldRouteToJiraPlansCustomUi
	) {
		return ActionPredicateKeys.Resumption;
	}

	return jiraPlansCustomUiActionKey ?? action.key;
};

const groupActionsByKey = (
	actions: MessageAction[],
	getActionGroupKey?: (action: MessageAction) => string,
): Record<string, MessageAction[]> => {
	return actions.reduce(
		(groups, action) => {
			const key = getActionGroupKey?.(action) || action.key;
			if (!groups[key]) {
				groups[key] = [];
			}
			groups[key].push(action);
			return groups;
		},
		{} as Record<string, MessageAction[]>,
	);
};

export const useCustomMessageActionBroker = (
	actions: MessageAction[],
	props: MessageActionProps,
	actionAnalytics?: {
		onErrorRender?: (
			action: MessageAction,
			{ error, info }: { error: Error; info: React.ErrorInfo },
		) => void;
		onSuccessRender?: (action: MessageAction) => void;
	},
): React.JSX.Element | null | undefined => {
	di(FlagsProvider, useActionRenderer, useIntl);

	const normalizedActions = jiraPlansActionData.normalizeCustomUiActions(actions);
	const shouldEvaluateRovoToolsResumptionMigration = normalizedActions.some(
		(action) =>
			ROVO_TOOLS_RESUMPTION_CONDITIONED_RENDERER_KEYS.has(action.key) ||
			(Boolean(action.toolSource) && ROVO_TOOLS_RESUMPTION_MIGRATED_RENDERER_KEYS.has(action.key)),
	);
	const isRovoToolsResumptionMigrationEnabled =
		shouldEvaluateRovoToolsResumptionMigration &&
		FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false);
	const getRovoToolsResumptionMigrationEnabled = useCallback(
		() => isRovoToolsResumptionMigrationEnabled,
		[isRovoToolsResumptionMigrationEnabled],
	);
	const { getActionRenderer } = useActionRenderer(getRovoToolsResumptionMigrationEnabled);

	if (!normalizedActions.length || !normalizedActions[0]) {
		return null;
	}

	const groupedActions = groupActionsByKey(normalizedActions, (action) =>
		getActionGroupKey(action, isRovoToolsResumptionMigrationEnabled),
	);

	const nodes = Object.entries(groupedActions).flatMap(([groupKey, actions]) => {
		const { ActionRenderer, ActionRendererFallback } = getActionRenderer(groupKey, actions);

		if (ActionRenderer) {
			const Fallback = ActionRendererFallback ?? DefaultActionRendererSkeleton;
			return [
				<ErrorBoundaryWithAnalytics
					key={groupKey}
					action={actions[0]}
					onError={actionAnalytics?.onErrorRender}
				>
					<Suspense
						interactionName={`rovo-conversation-assistant-message-action-${groupKey}`}
						fallback={<Fallback />}
					>
						<ActionRenderer actions={actions} {...props} />
						<SuccessEventComponent
							action={actions[0]}
							onSuccess={actionAnalytics?.onSuccessRender}
						/>
					</Suspense>
				</ErrorBoundaryWithAnalytics>,
			];
		}

		return [];
	});

	if (nodes.length > 0) {
		return (
			<FlagsProvider>
				<Stack space="space.200">{nodes}</Stack>
			</FlagsProvider>
		);
	}

	return undefined;
};

// used for testing only
export const customMessageActionKeys: string[] = [
	JIRA_FIND_ISSUES_KEY,
	JIRA_EPICS_SUGGEST_KEY,
	JIRA_ISSUE_DELETE_KEY,
	JIRA_ISSUE_MOVE_KEY,
];

const isConfluenceRenamePageActionEnabled =
	// eslint-disable-next-line @atlaskit/platform/no-module-level-eval, @atlaskit/platform/use-recommended-utils
	FeatureGates.getExperimentValue(
		'confluence_rename_page_rovo_action_custom_ui',
		'isEnabled',
		false,
		{
			fireExperimentExposure: false,
		},
	);

const isConfluenceAddLabelActionEnabled =
	// eslint-disable-next-line @atlaskit/platform/no-module-level-eval, @atlaskit/platform/use-recommended-utils
	FeatureGates.getExperimentValue(
		'confluence_add_page_label_rovo_action_custom_ui',
		'isEnabled',
		false,
		{
			fireExperimentExposure: false,
		},
	);

const isConfluencePageMoveActionEnabled =
	// eslint-disable-next-line @atlaskit/platform/no-module-level-eval, @atlaskit/platform/use-recommended-utils
	FeatureGates.getExperimentValue(
		'confluence_move_page_rovo_action_custom_ui',
		'isEnabled',
		false,
		{
			fireExperimentExposure: false,
		},
	);

export const getActionKeysToNotRenderLLMContent = (): (string | boolean)[] => [
	CONFLUENCE_PAGE_CREATE_KEY,
	CONFLUENCE_PAGE_COMMENT_KEY,
	isConfluenceRenamePageActionEnabled && CONFLUENCE_PAGE_RENAME_KEY,
	isConfluenceAddLabelActionEnabled && CONFLUENCE_PAGE_ADD_LABEL_KEY,
	isConfluencePageMoveActionEnabled && CONFLUENCE_PAGE_MOVE_KEY,
	fg('rovo_chat_store_recommendation_action') && STORE_RECOMMENDATION_ACTION_KEY,
	fg('surveys-aifc') && JSM_CREATE_SURVEY_KEY,
	fg('display_components_for_create-focus-ask_skill') && FOCUS_CREATE_ASK_KEY,
	fg('rovo_chat_jira_create_view_skill') && JIRA_VIEW_CREATE_AND_CONFIGURE_KEY,
];

export const shouldNotRenderLLMContentForAction = (action: MessageAction): boolean => {
	if (getActionKeysToNotRenderLLMContent().includes(action.key)) {
		return true;
	}

	return jiraPlansActionData.shouldNotRenderLLMContentForAction(action);
};
